import React, { useState, useEffect, useCallback } from 'react';
import FeedbackModal from './FeedbackModal';
import './HelpDrawer.css';

const CALENDLY_URL = 'https://calendly.com/daniel-foobalabs/30min';
const REDDIT_URL = 'https://www.reddit.com/r/optionsleveling';
const EMAIL = 'daniel@foobalabs.com';

function HelpDrawer({ isOpen, onClose }) {
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') onClose();
  }, [onClose]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleKeyDown]);

  return (
    <>
      {/* Backdrop */}
      <div
        className={`help-drawer-backdrop ${isOpen ? 'open' : ''}`}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Panel */}
      <div
        className={`help-drawer-panel ${isOpen ? 'open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Help panel"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="help-drawer-header">
          <div className="help-drawer-header-top">
            <h2 className="help-drawer-title">Need Help or Have Feedback?</h2>
            <button
              className="help-drawer-close"
              onClick={onClose}
              aria-label="Close help panel"
            >
              <svg
                width="20"
                height="20"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
          <div className="help-drawer-subtitle">
            <p>
              I built OptionsLeveling because I was tired of wasting hours running option scenarios,
              flipping between tabs, and second-guessing my math.
            </p>
            <p>
              I'm not a big company. I'm a trader who wanted a faster way to think through
              contracts and risk.
            </p>
            <p>
              If something feels confusing, slow, or missing — I genuinely want to know. This
              product gets better every time someone tells me what would actually save them time.
            </p>
          </div>
        </div>

        {/* Content */}
        <div className="help-drawer-content">
          {/* Section 1: Schedule a Call */}
          <div className="help-drawer-section">
            <h3 className="help-drawer-section-title">
              Schedule a 30-Min Call with Me
            </h3>
            <p className="help-drawer-section-description">
              If you want to talk through how you're using the tool, what's unclear, or what you
              wish it did — book a 30-minute call.
            </p>
            <p className="help-drawer-section-description">
              These conversations directly shape what I build next.
            </p>
            <a
              href={CALENDLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="help-drawer-btn-primary"
            >
              Schedule a 30-Min Call
            </a>
          </div>

          {/* Section 2: Email Me Directly */}
          <div className="help-drawer-section">
            <h3 className="help-drawer-section-title">Email Me Directly</h3>
            <p className="help-drawer-section-description">
              Bug report. Feature idea. Something annoying. Something you love.
            </p>
            <p className="help-drawer-section-description">
              Send it straight to me.
            </p>
            <p className="help-drawer-email">{EMAIL}</p>
            <button
              className="help-drawer-btn-secondary"
              onClick={() => setFeedbackOpen(true)}
            >
              Email Daniel
            </button>
          </div>

          {/* Section 3: Join the Community */}
          <div className="help-drawer-section">
            <h3 className="help-drawer-section-title">Join the Community</h3>
            <p className="help-drawer-section-description">
              If you'd rather share ideas publicly, r/optionsleveling is where I post updates
              and gather feedback from other users.
            </p>
            <p className="help-drawer-section-description" style={{ marginBottom: 0 }}>
              It's all built in public.
            </p>
            <a
              href={REDDIT_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="help-drawer-btn-secondary"
              style={{ marginTop: '12px' }}
            >
              Visit r/optionsleveling
            </a>
          </div>
        </div>
      </div>

      <FeedbackModal
        isOpen={feedbackOpen}
        onClose={() => setFeedbackOpen(false)}
      />
    </>
  );
}

export default HelpDrawer;
